import React, { useEffect, useState } from "react";
import {
	Box,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Avatar,
	Typography,
} from "@mui/material";
import axiosInstance from "src/Helpers/utils/axios";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IAdminFair } from "src/Types/module/fair/adminFair";
import { imgUrl } from "src/Helpers/utils/constants";

const FairSelect = () => {
	const { data: fairs = [] } = useApiQuery<IAdminFair[]>({
		endPoint: `/api/fair`,
		queryKeys: ["Fairs"],
	});
	const queryClient = useQueryClient();
	const [year, setYear] = useState<string>(() => {
		return typeof window !== "undefined"
			? localStorage.getItem("selected_fair_id") || ""
			: "";
	});

	useEffect(() => {
		if (fairs && fairs.length > 0) {
			const savedFairId = localStorage.getItem("selected_fair_id");
			const matchedFair = fairs.find(
				(f) => String(f.fair_id) === String(savedFairId)
			);
			// Default to 0th index fair if no valid saved fair found
			const defaultFair = matchedFair || fairs[0];

			if (defaultFair) {
				const defaultIdStr = String(defaultFair.fair_id);
				setYear(defaultIdStr);
				axiosInstance.defaults.headers.common["fairid"] = defaultFair.fair_id;
				localStorage.setItem("selected_fair_id", defaultIdStr);
			}
		}
	}, [fairs]);

	const handleChange = (event: any) => {
		const selectedVal = String(event.target.value);
		setYear(selectedVal);
		localStorage.setItem("selected_fair_id", selectedVal);
		axiosInstance.defaults.headers.common["fairid"] = selectedVal;
		queryClient.invalidateQueries();
	};

	return (
		<FormControl fullWidth size="small">
			<InputLabel id="year-select-label">Select Fair</InputLabel>
			<Select
				labelId="year-select-label"
				id="year-select"
				value={year}
				onChange={handleChange}
				label="Select Fair"
				renderValue={(selected) => {
					const item = fairs?.find(
						(f) => String(f.fair_id) === String(selected)
					);
					if (!item) return "";
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
							{item.fair_main_banner ? (
								<Avatar
									alt={item.fair_name}
									src={imgUrl + "/" + item.fair_main_banner}
									sx={{
										width: 22,
										height: 22,
										borderRadius: "4px",
										flexShrink: 0,
									}}
								/>
							) : (
								<Avatar
									sx={{
										width: 22,
										height: 22,
										fontSize: "0.75rem",
										borderRadius: "4px",
										flexShrink: 0,
										bgcolor: "primary.main",
									}}
								>
									{item.fair_name?.charAt(0)}
								</Avatar>
							)}
							<Typography
								variant="body2"
								fontWeight={600}
								noWrap
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									fontSize: { xs: "0.82rem", sm: "0.88rem" },
								}}
							>
								{item.fair_name}
							</Typography>
						</Box>
					);
				}}
			>
				{fairs?.map((item, i) => (
					<MenuItem
						key={item.fair_id || i}
						value={String(item.fair_id)}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.2,
							py: 1,
						}}
					>
						{item.fair_main_banner ? (
							<Avatar
								alt={item.fair_name}
								src={imgUrl + "/" + item.fair_main_banner}
								sx={{
									width: 24,
									height: 24,
									borderRadius: "4px",
									flexShrink: 0,
								}}
							/>
						) : (
							<Avatar
								sx={{
									width: 24,
									height: 24,
									fontSize: "0.75rem",
									borderRadius: "4px",
									flexShrink: 0,
									bgcolor: "primary.main",
								}}
							>
								{item.fair_name?.charAt(0)}
							</Avatar>
						)}
						<Typography variant="body2" fontWeight={500}>
							{item.fair_name}
						</Typography>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

export default FairSelect;

